import type { Strings } from '../index.ts';

/**
 * Die Einstellungsseite — der größte Abschnitt, und der, in dem die Wörter *die Funktion sind*.
 *
 * Das ist auch die eine Seite, auf der eine Tatsache über die Instanz *das Thema* ist und nicht Rauschen:
 * wer eine Adresse in das Feld tippt und Prüfen drückt, stellt eine technische Frage und verdient eine
 * technische Antwort. Alles andere hält sich an dieselbe Regel wie der Rest der App.
 *
 * Drei Stellen sind Satzfragmente und keine Sätze, weil die View sie einbaut:
 *
 * - **`instanceSource`** hängt hinter „Verbunden mit **host**“ und beginnt deshalb klein.
 * - **`instance.stepSkipped` / `stepNoAnswer`** stehen in einer Ergebnisspalte neben `HTTP 200`.
 * - **`control.language.*`** sind ganze Sätze und nehmen den Sprachnamen als Objekt: „Die Oberfläche ist
 *   auf Deutsch“ braucht den Namen im Nominativ hinter „auf“, was für alle Sprachnamen gilt.
 */
export const settings: Strings['settings'] = {
  heading: 'Einstellungen',
  lede: 'Wo deine Instanz eine passende Einstellung hat, siehst du hier den Wert aus deinem Konto, und Änderungen werden direkt dorthin gespeichert. Der Rest liegt in diesem Browser. Das kleine Zeichen hinter jedem Namen sagt, was was ist.',

  storage: {
    account: 'Folgt deinem Konto, wenn du angemeldet bist.',
    browser: 'Bleibt in diesem Browser.',
  },

  tab: {
    playback: 'Wiedergabe',
    interface: 'Oberfläche',
    integrations: 'Dienste',
    instance: 'Instanz',
    data: 'Daten',
  },
  tabsLabel: 'Einstellungsbereiche',

  instanceSource: {
    'url-param': 'aus der Adresszeile, nur für diesen Seitenaufruf',
    'saved-setting': 'in diesem Browser gespeichert',
    'config-json': 'die Voreinstellung für diese Seite',
    'build-default': 'der eingebaute Rückfall',
  },

  instanceUrlProblem: {
    empty: 'Gib die Adresse einer Invidious-Instanz ein.',
    notAbsolute:
      'Nimm das Schema mit: https://invidious.example.com, nicht invidious.example.com.',
    unsupportedScheme: 'Es lassen sich nur http:// und https:// verwenden.',
    hasQuery: 'Gib nur die Adresse an, ohne etwas dahinter.',
  },

  instance: {
    resolving: 'Wird aufgelöst…',
    connectedTo: 'Verbunden mit',
    addressLabel: 'Instanz-Adresse',
    addressPlaceholder: 'https://invidious.example.com',
    check: 'Prüfen',
    checking: 'Wird geprüft…',
    switch: 'Wechseln',
    running: 'Die fünf Prüfungen laufen…',
    checkFailed: 'Die Prüfung ließ sich nicht abschließen.',
    saveRefused:
      'Dieser Browser wollte die Instanz nicht speichern, deshalb lässt sie sich nicht wechseln.',
    clearRefused: 'Dieser Browser wollte die gespeicherte Instanz nicht löschen.',
    usable: 'Ein Video lädt, diese Instanz kann die App also bedienen.',
    unusable:
      'Das Testvideo hat nicht geladen, diese Instanz kann die App also nicht bedienen. Diese Prüfung ist die entscheidende. Nichts anderes darunter ändert das.',
    stepSkipped: 'übersprungen',
    stepNoAnswer: 'keine Antwort',
    software: 'Software',
    useDefault: 'Wieder die Voreinstellung nehmen',
    openPreferences: 'Die eigenen Einstellungen deiner Instanz öffnen',
    openPreferencesDetail:
      'Einstellungen auf Instanz-Ebene, über die Visious keine Kontrolle hat, auf der Seite der Instanz selbst.',
    switchWarning:
      'Konten gelten pro Instanz, samt der zugehörigen Abos, Playlists, des Verlaufs und einiger Einstellungen. Ein Wechsel meldet dich ab und entfernt diese Daten aus Visious.',
  },

  probeStep: {
    stats: '1. Prüfen, ob es Invidious ist, und welche Version',
    'public-api': '2. Prüfen, ob die API antwortet',
    video: '3. Prüfen, ob ein Video lädt',
    features: '4. Prüfen, was die Instanz anbietet',
    captions: '5. Prüfen, ob Untertitel ankommen',
  },

  probeFinding: {
    unreachable: 'Überhaupt keine Antwort. Der Host ist aus, oder die Adresse ist falsch.',
    corsBlocked:
      'Die Instanz hat geantwortet, aber nicht dieser Seite: ihre CORS-Einstellungen erlauben die Adresse nicht, auf der du bist. Das ist eine Einstellung auf der Instanz und nichts, was Visious umgehen kann.',
    aborted: 'Die Prüfung wurde abgebrochen.',
    httpError: 'Sie hat mit einem Fehler geantwortet statt mit einer Antwort.',
    emptyBody: 'Sie hat mit gar nichts geantwortet.',
    notJson:
      'Was da geantwortet hat, sieht nicht wie Invidious aus. Meist ist es ein Webserver oder ein Proxy davor.',
    notInvidious: 'Was dort läuft, gibt sich nicht als Invidious aus.',
    noAdaptiveFormats: 'Das Testvideo bietet keine Auswahl an Qualitätsstufen.',
    noDashManifest: 'Das Testvideo hat keinen adaptiven Stream.',
    manifestUnreadable:
      'Der Stream des Testvideos ließ sich nicht lesen. Vor dem Glauben lohnt ein zweiter Versuch, denn diese Instanz bekommt ein paar Prozent davon zufällig falsch.',
    noCaptionsListed: 'Das Testvideo hat keine Untertitel, das ließ sich also nicht prüfen.',
    popularDisabled:
      'Die Administration hat Beliebt abgeschaltet. Die Startseite fällt auf Trends zurück.',
    authAbsent:
      'Diese Instanz hat keine Konten, deshalb sind Anmeldung, Abos, Playlists und Verlauf nicht verfügbar.',
    captionsDocumentedPath:
      'Untertitel kommen an und lassen sich zeigen, über einen Weg, den die meisten Instanzen leer beantworten. Ungewöhnlich, und in Ordnung.',
    captionsUnavailable: 'Es kamen keine Untertitel an, deshalb werden sie versteckt.',
  },

  capability: {
    serves: 'Ja',
    disabled: 'Nein',
    unknown: 'Nicht bekannt',
    popular: 'Beliebt-Feed',
    popularDisabled: 'Die Startseite zeigt stattdessen Trends.',
    trending: 'Trends-Feed',
    adaptiveFormats: 'Auswahl an Qualitätsstufen',
    dashManifest: 'Adaptives Streaming',
    auth: 'Konten und Anmeldung',
    captions: 'Untertitel',
    captionsHidden: 'Der Untertitel-Knopf wird versteckt.',
  },

  control: {
    quality: {
      label: 'Standardqualität',
      hint: 'Hat ein Video diese Qualität nicht, läuft die nächstgelegene, die es hat.',
      auto: 'Adaptiv (empfohlen)',
      height: (height) => `${height}p`,
    },
    speed: {
      label: 'Standardgeschwindigkeit',
      /* Punkt zu Komma: `${rate}` liefert die JS-Zahlendarstellung, und die kennt nur den Punkt. */
      rate: (rate) => `${String(rate).replace('.', ',')}×`,
    },
    audioLanguage: {
      label: 'Bevorzugte Tonsprache',
      detail: 'Videos mit mehr als einer Tonspur starten in dieser Sprache.',
      original: 'Original',
    },
    subtitleLanguage: {
      label: 'Bevorzugte Untertitel',
      off: 'Aus',
      detail: 'Untertitel gehen in dieser Sprache an, wenn ein Video sie hat.',
    },
    youtubeEmbed: {
      label: 'Zu YouTube wechseln',
      detail: 'Fügt einen Weg hinzu, den Player durch ein eingebettetes YouTube-Video zu ersetzen.',
      detailTitle:
        'Fügt dem Abschnitt „Player wechseln“ im Menü „Ansehen auf“ der Watch-Seite einen YouTube-Eintrag hinzu. Ein Druck darauf ersetzt den Player durch YouTubes eigenen eingebetteten, samt allen Bedienelementen, an derselben Stelle im Video, mit einem Weg zurück zu Invidious an seiner Stelle.',
    },
    autoplay: {
      label: 'Beim Ankommen abspielen',
      detail: 'Der Browser hat das letzte Wort und startet vielleicht stumm.',
      detailTitle:
        'Chrome blockiert die Wiedergabe mit Ton, bis du mit der Seite umgegangen bist, und Visious startet dann stumm statt vorzugeben, es hätte geklappt.',
    },
    useStableVolume: {
      label: 'Tonspuren mit gleichmäßiger Lautstärke verwenden',
      detail: 'Spielt die ausgeglichene Fassung der Tonspur, wo ein Video eine hat.',
      detailTitle:
        'YouTube bereitet von manchen Tonspuren eine Fassung mit ausgeglichener Lautheit vor. Das Tonmenü des Players hat denselben Schalter, und ihn dort umzulegen ändert auch diese Einstellung.',
    },
    forceAvc: {
      label: 'H.264 erzwingen',
      detail: 'Für Hardware, die AV1 nicht dekodieren kann.',
      detailTitle: 'Wirkt beim nächsten Video, das du öffnest.',
    },
    resumePlayback: {
      label: 'Da weitermachen, wo du aufgehört hast',
      detail:
        'Ein Video zu öffnen macht dort weiter, wo du aufgehört hast, und die Vorschaubilder zeigen, wie weit du gekommen bist.',
      detailTitle:
        'Ein Zeitstempel in einem Link gewinnt immer gegen die Stelle, an der du aufgehört hast.',
    },
    region: {
      label: 'Inhaltsregion',
      hint: 'Was Trends zeigt. Ohne Vorgabe nimmt deine Instanz ihre eigene.',
      none: 'Keine Vorgabe',
    },
    landing: {
      label: 'Startseite',
      hint: 'Wohin Visious dich beim Öffnen bringt. Der Markenlink in der Kopfzeile führt auch dorthin.',
      popular: 'Beliebt',
      subscriptions: 'Abos',
      playlists: 'Playlists',
      history: 'Verlauf',
    },
    trendingOnPopular: {
      label: 'Trends auf der Beliebt-Seite',
      detail: 'Fügt über den beliebten Videos einen Trends-Abschnitt hinzu.',
      detailTitle:
        'Die Trendliste hat keine eigene Seite. Das anzuschalten setzt sie oben auf die Beliebt-Seite, mit ihrer eigenen Suche.',
    },
    menu: {
      label: 'Menübereiche',
      detail: 'Einen zu verstecken räumt nur das Menü auf. Seine Seite funktioniert weiter.',
      detailTitle:
        'Welche Bereiche das Menü zeigt. Die Seite eines versteckten Bereichs funktioniert weiter, und die Startseite kann weiterhin darauf zeigen.',
      allShown: 'Alle sichtbar',
      shownOf: (shown, total) => `${shown} von ${total} sichtbar`,
      popularDisabled:
        'Deine Instanz hat den Beliebt-Feed abgeschaltet, deshalb gibt es für diesen Eintrag nichts zu zeigen.',
    },
    theme: {
      label: 'Design',
      hint: 'Dem System zu folgen heißt, es wechselt mit deinem Schreibtisch, ohne neu zu laden.',
      system: 'Dem System folgen',
      dark: 'Dunkel',
      light: 'Hell',
    },
    gridPreviews: {
      label: 'Video-Vorschau',
      detail:
        'Den Zeiger auf einem Vorschaubild ruhen zu lassen zeigt eine Vorschau, und die Leiste darunter spult darin.',
      detailTitle:
        'Den Zeiger einen Moment auf dem Vorschaubild einer Videokarte ruhen zu lassen blättert durch die Vorschaubilder des Videos. Den Zeiger entlang der Leiste an der Unterkante zu bewegen spult in der Vorschau.',
    },
    miniPlayer: {
      label: 'Weiterspielen, wenn du das Video verlässt',
      detail: 'Das Video wandert in einen kleinen Player in der Ecke statt aufzuhören.',
      detailTitle:
        'Die Watch-Seite zu verlassen, während ein Video läuft, behält es in einem kleinen Player, in der unteren Ecke oder als Streifen über dem Menü auf einem schmalen Bildschirm.',
    },
    watchLater: {
      label: '„Später ansehen“-Playlist',
      hint: 'Die Playlist, in die die „Später ansehen“-Knöpfe speichern.',
      hintTitle:
        'Die „Später ansehen“-Knöpfe auf Videokarten und auf der Watch-Seite speichern in diese Playlist.',
      none: 'Keine gewählt',
      choose: 'Wählen…',
    },
    mirrorHistory: {
      label: 'Aufzeichnen, was du ansiehst, in deinem Konto',
      detail: 'Aus heißt, nichts von dem, was du ansiehst, wird auf der Instanz markiert.',
      detailTitle:
        'Aus heißt, nichts von dem, was du ansiehst, wird je auf der Instanz behalten, und die Verlaufsseite zeigt nur, was schon dort ist.',
    },
    language: {
      label: 'Sprache',
      followSystem: 'Dem System folgen',
      en: 'Englisch',
      de: 'Deutsch',
      hintSetting: (language) => `Die Oberfläche ist auf ${language}, weil du das gewählt hast.`,
      hintSystem: (language) =>
        `${language} ist die erste Sprache auf der Liste deines Systems, die Visious hat, deshalb ist die Oberfläche in dieser Sprache.`,
      hintFallback: (language) =>
        `Dein System fragt nach einer Sprache, von der Visious noch keine Fassung hat, deshalb ist die Oberfläche auf ${language}.`,
      formatting: (locale) => `Datum und Zahlen verwenden ${locale}.`,
    },
  },

  sponsorBlock: {
    title: 'SponsorBlock',
    lede: 'Abschnitte, die andere Zuschauer in Videos markiert haben: Sponsorenlesungen, Intros, Erinnerungen zu abonnieren. Visious kann sie auf die Fortschrittsleiste zeichnen, Kapitel markieren, die sie ausfüllen, und sie überspringen. Die Abfragen sind anonym, weil an den Dienst ein Hash geht und nie das Video selbst.',
    enable: {
      label: 'SponsorBlock verwenden',
      detail: 'Für jedes Video, das du öffnest, Abschnitte abfragen.',
    },
    address: {
      label: 'Dienst-Adresse',
      detail: 'Leer lassen für den offiziellen Dienst, sponsor.ajay.app.',
      notAbsolute: 'Nimm das Schema mit: https://sponsor.example.com, nicht sponsor.example.com.',
    },
    behavior: {
      auto: 'Automatisch überspringen',
      toast: 'Einen Überspringen-Knopf anbieten',
      off: 'Nichts tun',
    },
    categories: {
      title: 'Kategorien',
      lede: 'Was mit jeder Art von Abschnitt geschehen soll, und wo er auftaucht: als Streifen auf der Fortschrittsleiste in der Farbe der Kategorie, und als Marke auf einem Kapitel, das ein Abschnitt zu mindestens drei Vierteln ausfüllt.',
      colorLabel: (category) => `Farbe für ${category}`,
      behaviorTitle: 'Verhalten beim Überspringen',
      showTitle: 'Anzeigen auf',
      bar: 'Fortschrittsleiste',
      chapters: 'Kapitel',
    },
  },

  deArrow: {
    title: 'DeArrow',
    lede: 'Von der Gemeinschaft geschriebene Titel und Vorschaubilder, die ein Video beschreiben statt es zu verkaufen, gezeigt auf Videokarten überall. Ein Video, das du schon ansiehst, behält seinen echten Titel. Titelabfragen sind anonym, an den Dienst geht ein Hash statt des Videos selbst.',
    enable: {
      label: 'DeArrow verwenden',
      detail: 'Titel auf Videokarten ersetzen, wo die Gemeinschaft einen geschrieben hat.',
    },
    thumbnails: {
      label: 'Vorschaubilder ersetzen',
      detail:
        'Auch von der Gemeinschaft gewählte Videobilder einsetzen. Anders als bei Titelabfragen geht beim Holen dieser Bilder die reine Video-ID an den Vorschaubild-Dienst.',
    },
    address: {
      label: 'Dienst-Adresse',
      detail: 'Leer lassen für den offiziellen Dienst, sponsor.ajay.app.',
    },
    thumbnailAddress: {
      label: 'Adresse des Vorschaubild-Diensts',
      detail: 'Leer lassen für den offiziellen Dienst, dearrow-thumb.ajay.app.',
    },
  },

  returnYoutubeDislike: {
    title: 'Return YouTube Dislike',
    lede: 'Eine geschätzte Anzahl von Dislikes, gezeigt neben der Anzahl der Likes auf der Watch-Seite. YouTube zeigt die echte Zahl nicht mehr öffentlich, deshalb ist das die Schätzung eines Gemeinschaftsprojekts. Eine Abfrage schickt die reine Video-ID an den Dienst.',
    enable: {
      label: 'Return YouTube Dislike verwenden',
      detail: 'Für jedes Video, das du öffnest, eine geschätzte Anzahl von Dislikes abfragen.',
    },
    address: {
      label: 'Dienst-Adresse',
      detail: 'Leer lassen für den offiziellen Dienst, returnyoutubedislikeapi.com.',
    },
  },

  peerTube: {
    title: 'PeerTube',
    lede: 'Ein Rückfall für den Fall, dass ein Video hier nicht läuft. Es sucht über Titel und Länge nach einem passenden Upload und bietet in beiden Abschnitten des Menüs „Ansehen auf“ der Watch-Seite einen Eintrag „PeerTube“ an. Unter „Player wechseln“ spielt er den Treffer hier anstelle des Videos ab, unter „Seite öffnen“ öffnet er ihn auf seiner eigenen Instanz.',
    enable: {
      label: 'PeerTube verwenden',
      detail: 'Nach einem passenden Video suchen, sobald das Menü „Ansehen auf“ geöffnet wird.',
    },
    address: {
      label: 'Such-Instanz',
      detail: 'Leer lassen für die Voreinstellung, sepiasearch.org.',
    },
    searchTarget: {
      label: 'Suchziel',
      detail: 'Nach welchen Videoquellen die Such-Instanz gefragt wird.',
      searchIndex: 'Verbundene Instanzen',
      local: 'Nur lokal',
    },
    mode: {
      label: 'Player',
      detail: 'Wie ein gefundenes Video läuft. Im integrierten Player, oder im eingebetteten von PeerTube.',
      integrated: 'Integriert',
      embedded: 'Eingebettet',
    },
  },

  language: {
    ar: 'Arabisch',
    bn: 'Bengalisch',
    de: 'Deutsch',
    en: 'Englisch',
    es: 'Spanisch',
    fr: 'Französisch',
    hi: 'Hindi',
    id: 'Indonesisch',
    it: 'Italienisch',
    ja: 'Japanisch',
    ko: 'Koreanisch',
    nl: 'Niederländisch',
    pl: 'Polnisch',
    pt: 'Portugiesisch',
    ru: 'Russisch',
    tr: 'Türkisch',
    vi: 'Vietnamesisch',
    zhHans: 'Chinesisch (vereinfacht)',
  },

  syncState: {
    title: 'Einstellungen abgleichen',
    signedOut: {
      status: 'Kein Abgleich',
      detail:
        'Gerade wird jede Einstellung nur im Browser gespeichert. Melde dich bei deiner Instanz an, um passende Einstellungen abzugleichen.',
    },
    syncing: {
      status: 'Dein Konto wird geprüft',
      detail:
        'Dein Konto wird gerade geprüft. Sobald das fertig ist, werden passende Einstellungen abgeglichen.',
    },
    synced: {
      status: 'Abgeglichen',
      detail: 'Passende Konto-Einstellungen werden mit deiner Instanz abgeglichen.',
    },
  },

  account: {
    lastSynced: (when) => `Zuletzt abgeglichen ${when}.`,
  },

  data: {
    importExport: {
      label: 'Deine Daten importieren oder exportieren',
      detail:
        'Abos, Playlists und Verlauf, hinein- oder herausbewegt über die eigenen Werkzeuge der Instanz.',
    },
    clearTitle: 'Daten löschen',
    clearLede: 'Jeder Knopf fragt noch einmal, bevor etwas gelöscht wird.',
    history: {
      label: 'Wiedergabeverlauf löschen',
      confirmTitle:
        'Löscht den Wiedergabeverlauf und die gespeicherten Stellen endgültig. Es gibt kein Zurück.',
    },
    state: {
      label: 'Lokalen Zustand löschen',
      confirmTitle: 'Setzt die Einstellungen und Anordnungen dieses Browsers zurück.',
    },
    cache: {
      label: 'Lokalen Zwischenspeicher löschen',
      confirmTitle:
        'Löscht behaltene Namen und zwischengespeicherte Kopien. Sie füllen sich von selbst wieder.',
    },
    auth: {
      label: 'Lokale Anmeldung löschen',
      confirmTitle: 'Löscht die gespeicherte Sitzung. Die neue Anmeldung kostet zwei Klicks.',
    },
    all: {
      label: 'Alles löschen',
      confirmTitle:
        'Löscht alles, was hier steht: Verlauf, Zustand, Zwischenspeicher und die Anmeldung.',
    },
    explain: {
      history: {
        term: 'Wiedergabeverlauf',
        text: 'Was du gesehen hast und wo du aufgehört hast. Die Liste der gesehenen Videos liegt in deinem Konto, und die Stellen, an denen ein Video weiterläuft, liegen in diesem Browser. Löschen entfernt beides endgültig. Abos, Playlists und Einstellungen bleiben.',
      },
      state: {
        term: 'Lokaler Zustand',
        text: 'Entscheidungen, die sich nur dieser Browser merkt, etwa versteckte Menübereiche oder die Anordnung der Watch-Seite. Löschen setzt sie auf die Voreinstellungen zurück.',
      },
      cache: {
        term: 'Lokaler Zwischenspeicher',
        text: 'Kopien, die aufbewahrt werden, damit Seiten sofort erscheinen, etwa Kanal- und Videonamen oder die Einstellungen deines Kontos. Löschen verliert nichts von dir. Alles hier füllt sich von selbst wieder.',
      },
      auth: {
        term: 'Lokale Anmeldung',
        text: 'Das Token, das dich hier angemeldet hält. Es zu löschen meldet diesen Browser ab und nichts weiter.',
      },
    },
  },

  preferences: {
    unwritable: {
      title: 'Visious hat diese Änderung nicht gesendet',
      detail:
        'Einer der Werte wäre nicht gespeichert worden, und die Instanz hätte trotzdem Erfolg gemeldet, es hätte also gespeichert ausgesehen und wäre es nicht gewesen. Es wurde nichts geändert. Das ist ein Fehler hier und nichts, was du getan hast.',
      retryable: false,
    },
    notFound: {
      title: 'Diese Instanz kann keine Einstellungen speichern',
      detail:
        'Es gibt nichts, womit sich abgleichen ließe. Deine Einstellungen funktionieren weiter, sie bleiben nur in diesem Browser.',
      retryable: false,
    },
    unreadable: {
      title: 'Die Einstellungen deines Kontos ließen sich nicht lesen',
      detail:
        'Die Instanz hat mit etwas geantwortet, das Visious nicht lesen kann. Deine lokalen Einstellungen sind davon unberührt.',
      retryable: true,
    },
  },
};
